import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice, getEffectivePrice } from '../utils/helpers';
import toast from 'react-hot-toast';
import './ProductDetailPage.css';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();

  // ALL useState hooks at top — never conditionally
  const [product, setProduct]       = useState(null);
  const [reviews, setReviews]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [mainImg, setMainImg]       = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isPreOrder, setIsPreOrder] = useState(false);
  const [preOrderType, setPreOrderType] = useState('50percent');
  const [qty, setQty]               = useState(1);
  const [tab, setTab]               = useState('description');
  const [rating, setRating]         = useState(5);
  const [comment, setComment]       = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  // ALL useEffect hooks at top — never conditionally
  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/products/${id}`),
      api.get(`/reviews/${id}`),
    ]).then(([pRes, rRes]) => {
      const p = pRes.data;
      setProduct(p);
      setReviews(rRes.data);
      // Auto-select NO_SIZE when product is sizeless
      if (p?.sizes?.length === 1 && p.sizes[0]?.label === 'NO_SIZE') {
        setSelectedSize('NO_SIZE');
      } else {
        setSelectedSize(null);
      }
    }).catch(err => {
      setError(err.message || 'Failed to load product');
    }).finally(() => setLoading(false));
  }, [id]);

  // ── Early returns AFTER all hooks ──────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div style={{ textAlign: 'center', padding: '100px', color: 'var(--text3)' }}>
      <p>⚠️ {error}</p>
      <button className="btn btn-primary" onClick={() => navigate('/products')} style={{ marginTop: 16 }}>
        Back to Products
      </button>
    </div>
  );

  if (!product) return (
    <div style={{ textAlign: 'center', padding: '100px', color: 'var(--text3)' }}>
      Product not found
    </div>
  );

  // ── Derived values (not hooks) ─────────────────────────────────
  const effectivePrice   = getEffectivePrice(product);
  const allMedia         = [product.thumbnail, ...(product.images || [])];
  const isNoSizeProduct  = product.sizes?.length === 1 && product.sizes[0]?.label === 'NO_SIZE';
  const noSizeObj        = isNoSizeProduct ? product.sizes[0] : null;
  const selectedSizeObj  = isNoSizeProduct
    ? noSizeObj
    : product.sizes?.find(s => s.label === selectedSize);

  const shippingCharge = 80;

  const totalPrice = (() => {
    if (isPreOrder) {
      const rate = preOrderType === '50percent' ? 0.5 : 1;
      return Math.ceil(effectivePrice * qty * rate) + shippingCharge;
    }
    return effectivePrice * qty + shippingCharge;
  })();

  // YouTube helper
  const getYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m) return m[1];
    }
    return null;
  };
  const youtubeId = getYouTubeId(product.youtubeUrl);

  // ── Handlers ───────────────────────────────────────────────────
  const handleSizeSelect = (size) => {
    setSelectedSize(size.label);
    setIsPreOrder(false);
  };

  const handleAddToCart = () => {
    if (!isNoSizeProduct && !selectedSize) { toast.error('Please select a size'); return; }
    if (isPreOrder && !selectedSizeObj?.preOrder) { toast.error('Pre-order not available'); return; }
    if (!isPreOrder && !selectedSizeObj?.available) { toast.error('This item is not available'); return; }
    addToCart(product, isNoSizeProduct ? 'ONE SIZE' : selectedSize, qty, isPreOrder, isPreOrder ? preOrderType : null);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login to leave a review'); return; }
    setReviewLoading(true);
    try {
      const { data } = await api.post(`/reviews/${id}`, { rating, comment });
      setReviews(prev => [data, ...prev]);
      setComment('');
      setRating(5);
      toast.success('Review submitted!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="product-detail-page">
      <div className="container">

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span onClick={() => navigate('/')} className="bc-link">Home</span>
          <span className="bc-sep">/</span>
          <span onClick={() => navigate('/products')} className="bc-link">Products</span>
          <span className="bc-sep">/</span>
          <span className="bc-link" onClick={() => navigate(`/products?category=${product.category}`)}>
            {product.category}
          </span>
          <span className="bc-sep">/</span>
          <span className="bc-current">{product.name}</span>
        </div>

        {/* Main Grid */}
        <div className="detail-grid">

          {/* ── Images ── */}
          <div className="detail-images">
            <div className="main-img-wrap">
              {product.videos?.length > 0 && mainImg >= allMedia.length ? (
                <video src={product.videos[mainImg - allMedia.length]} controls className="main-img" />
              ) : (
                <img
                  src={allMedia[mainImg] || 'https://via.placeholder.com/500x500?text=NORIX'}
                  alt={product.name}
                  className="main-img"
                />
              )}
            </div>
            <div className="thumb-row">
              {allMedia.map((img, i) => (
                <img key={i} src={img} alt="" className={`thumb ${mainImg === i ? 'active' : ''}`} onClick={() => setMainImg(i)} />
              ))}
              {product.videos?.map((_, i) => (
                <div
                  key={`v${i}`}
                  className={`thumb video-thumb ${mainImg === allMedia.length + i ? 'active' : ''}`}
                  onClick={() => setMainImg(allMedia.length + i)}
                >▶</div>
              ))}
            </div>
          </div>

          {/* ── Info ── */}
          <div className="detail-info">
            <div className="detail-cat-tag">{product.category}</div>
            <h1 className="detail-name">{product.name}</h1>

            {/* Rating */}
            <div className="detail-rating">
              <span className="stars-display">
                {'★'.repeat(Math.round(product.rating || 0))}
                {'☆'.repeat(5 - Math.round(product.rating || 0))}
              </span>
              <span className="rating-val">{(product.rating || 0).toFixed(1)}</span>
              <span className="rating-count">({product.numReviews || 0} reviews)</span>
            </div>

            {/* Price */}
            <div className="detail-price-block">
              <span className="detail-price">{formatPrice(effectivePrice)}</span>
              {effectivePrice < product.price && (
                <>
                  <span className="detail-original">{formatPrice(product.price)}</span>
                  <span className="detail-save">Save {formatPrice(product.price - effectivePrice)}</span>
                </>
              )}
            </div>

            {/* Size selector — only for sized products */}
            {!isNoSizeProduct && (
              <div className="size-section">
                <div className="size-header">
                  <span className="size-label">Select Size</span>
                  {selectedSize && <span className="selected-size-tag">{selectedSize}</span>}
                </div>
                <div className="sizes-grid">
                  {product.sizes?.map(size => (
                    <div
                      key={size.label}
                      className={`size-btn-wrap ${selectedSize === size.label ? 'selected' : ''} ${!size.available && !size.preOrder ? 'unavailable' : ''}`}
                    >
                      <button
                        className="size-btn"
                        disabled={!size.available && !size.preOrder}
                        onClick={() => handleSizeSelect(size)}
                      >
                        {size.label}
                      </button>
                      <div className="size-indicators">
                        {size.available && <span className="size-ind available" title="Available">✓</span>}
                        {size.preOrder  && <span className="size-ind preorder"  title="Pre-Order">P</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="size-legend">
                  <span><span className="size-ind available">✓</span> Available</span>
                  <span><span className="size-ind preorder">P</span> Pre-Order</span>
                </div>
              </div>
            )}

            {/* No-size availability badges */}
            {isNoSizeProduct && (
              <div className="nosize-availability">
                {noSizeObj?.available && <span className="nosize-badge avail">✅ In Stock</span>}
                {noSizeObj?.preOrder  && <span className="nosize-badge pre">🔖 Pre-Order Available</span>}
                {!noSizeObj?.available && !noSizeObj?.preOrder && (
                  <span className="nosize-badge out">❌ Out of Stock</span>
                )}
              </div>
            )}

            {/* Pre-Order options */}
            {selectedSizeObj?.preOrder && (
              <div className="preorder-section">
                <div className="preorder-toggle">
                  <label className="toggle-wrap">
                    <input type="checkbox" checked={isPreOrder} onChange={e => setIsPreOrder(e.target.checked)} />
                    <span className="toggle-track"><span className="toggle-thumb" /></span>
                    <span>Order as Pre-Order</span>
                  </label>
                </div>
                {isPreOrder && (
                  <div className="preorder-options">
                    <p className="preorder-note">📦 Pre-order requires advance payment:</p>
                    <div className="preorder-choice">
                      <label className={`po-option ${preOrderType === '50percent' ? 'active' : ''}`}>
                        <input type="radio" name="pot" value="50percent" checked={preOrderType === '50percent'} onChange={e => setPreOrderType(e.target.value)} />
                        <div>
                          <strong>50% Advance</strong>
                          <span>Pay {formatPrice(Math.ceil(effectivePrice * qty * 0.5) + shippingCharge)} now</span>
                        </div>
                      </label>
                      <label className={`po-option ${preOrderType === '100percent' ? 'active' : ''}`}>
                        <input type="radio" name="pot" value="100percent" checked={preOrderType === '100percent'} onChange={e => setPreOrderType(e.target.value)} />
                        <div>
                          <strong>100% Advance</strong>
                          <span>Pay {formatPrice(effectivePrice * qty + shippingCharge)} now</span>
                        </div>
                      </label>
                    </div>
                    <p className="delivery-note">🚚 Delivery: Inside Dhaka ৳80 | Outside Dhaka ৳150</p>
                  </div>
                )}
              </div>
            )}

            {/* Quantity */}
            <div className="qty-section">
              <span className="qty-label">Quantity</span>
              <div className="qty-ctrl">
                <button className="qty-btn" onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                <span className="qty-num">{qty}</span>
                <button className="qty-btn" onClick={() => setQty(q => q + 1)}>+</button>
              </div>
            </div>

            {/* Price Summary */}
            <div className="price-summary">
              {isPreOrder ? (
                <>
                  <div className="price-row">
                    <span>Product ({preOrderType === '50percent' ? '50%' : '100%'} Advance)</span>
                    <span>{formatPrice(Math.ceil(effectivePrice * qty * (preOrderType === '50percent' ? 0.5 : 1)))}</span>
                  </div>
                  <div className="price-row"><span>Shipping (Dhaka)</span><span>{formatPrice(shippingCharge)}</span></div>
                  <div className="price-row total"><span>Total Advance</span><span>{formatPrice(totalPrice)}</span></div>
                </>
              ) : (
                <>
                  <div className="price-row"><span>Product</span><span>{formatPrice(effectivePrice * qty)}</span></div>
                  <div className="price-row"><span>Shipping (Dhaka)</span><span>{formatPrice(shippingCharge)}</span></div>
                  <div className="price-row total"><span>Total</span><span>{formatPrice(totalPrice)}</span></div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="detail-actions">
              <button className="btn btn-primary detail-add-btn" onClick={handleAddToCart}>🛒 Add to Cart</button>
              <button className="btn btn-secondary detail-buy-btn" onClick={handleBuyNow}>⚡ Buy Now</button>
            </div>

            {/* Meta tags */}
            <div className="detail-meta">
              <span className="meta-tag">🚚 Fast Delivery</span>
              <span className="meta-tag">✅ Authentic</span>
              <span className="meta-tag">💎 Premium Quality</span>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="product-tabs">
          <div className="tab-header">
            {['description', ...(youtubeId ? ['video'] : []), 'reviews'].map(t => (
              <button
                key={t}
                className={`tab-btn ${tab === t ? 'active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'description' ? '📋 Description'
                  : t === 'video' ? '▶️ Video'
                  : `💬 Reviews (${reviews.length})`}
              </button>
            ))}
          </div>

          {tab === 'description' && (
            <div className="tab-content description-tab">
              <p>{product.description}</p>
            </div>
          )}

          {tab === 'video' && youtubeId && (
            <div className="tab-content video-tab">
              <div className="yt-embed-wrap">
                <div className="yt-embed-header">
                  <div className="yt-embed-badge"><span className="yt-dot" /> YouTube Video</div>
                  <a href={product.youtubeUrl} target="_blank" rel="noopener noreferrer" className="yt-watch-link">
                    Watch on YouTube ↗
                  </a>
                </div>
                <div className="yt-player-box">
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                    title={product.name}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="yt-player"
                  />
                </div>
              </div>
            </div>
          )}

          {tab === 'reviews' && (
            <div className="tab-content reviews-tab">
              {user ? (
                <form onSubmit={submitReview} className="review-form">
                  <h3>Write a Review</h3>
                  <div className="star-picker">
                    {[1,2,3,4,5].map(s => (
                      <span key={s} className={`star-pick ${s <= rating ? 'filled' : ''}`} onClick={() => setRating(s)}>★</span>
                    ))}
                  </div>
                  <textarea
                    className="form-input"
                    rows={3}
                    placeholder="Share your experience..."
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    required
                    style={{ resize: 'vertical' }}
                  />
                  <button type="submit" className="btn btn-primary" disabled={reviewLoading}>
                    {reviewLoading ? 'Submitting...' : 'Submit Review'}
                  </button>
                </form>
              ) : (
                <div className="login-to-review">
                  <p>Please <a href="/login">login</a> to write a review</p>
                </div>
              )}
              <div className="reviews-list">
                {reviews.length === 0 ? (
                  <p className="no-reviews">No reviews yet. Be the first!</p>
                ) : reviews.map(rev => (
                  <div key={rev._id} className="review-item">
                    <div className="rev-header">
                      <div className="rev-user-avatar">{rev.name?.charAt(0).toUpperCase()}</div>
                      <div>
                        <strong>{rev.name}</strong>
                        <div className="rev-star-row">
                          {'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}
                          <span className="rev-date">{new Date(rev.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <p className="rev-text">{rev.comment}</p>
                    {rev.photo && <img src={rev.photo} alt="review" className="rev-img" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ProductDetailPage;
